import * as React from "react";
import {
    makeStyles,
    mergeClasses,
    Radio,
    RadioGroup,
    RadioGroupOnChangeData,
    RadioGroupProps,
    RadioProps,
    Text,
    tokens,
} from "@fluentui/react-components";

const useStyles = makeStyles({
    radioGroup: {
        userSelect: "none",
        display: "flex",
        flexDirection: "column",
        rowGap: "10px",
    },
    sectionTitle: {
        color: tokens.colorBrandForegroundLink,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: "5px",
    },
    hintText: {
        marginTop: tokens.spacingVerticalXS,
        fontStyle: "italic",
        color: tokens.colorNeutralForeground3,
    },
    option: {
        boxSizing: "border-box",
        maxWidth: "100%",
        display: "flex",
        alignItems: "center",
        fontSize: tokens.fontSizeBase300,
        overflowWrap: "break-word",
        whiteSpace: "normal",
        transitionProperty: "background-color",
        transitionDuration: "0.15s",
        borderRadius: tokens.borderRadiusSmall,
        ":hover": {
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
        "& label": {
            flex: 1,
            minWidth: 0,
            overflowWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "normal",
        },
    },
});

interface NewformaRadioProps extends Pick<RadioProps, "label" | "value" | "disabled" | "name" | "className" | "style"> {
    onChange?: RadioProps["onChange"];
    dataTestId?: string;
}

const NewformaRadio: React.FC<NewformaRadioProps> = ({
    label,
    value,
    disabled,
    name,
    className,
    style,
    onChange,
    dataTestId,
}) => {
    return (
        <Radio
            data-testid={dataTestId}
            label={label}
            value={value}
            disabled={disabled}
            name={name}
            className={className}
            style={style}
            onChange={onChange}
        />
    );
};

export interface NewformaRadioOption {
    value: string;
    label?: React.ReactNode;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export interface NewformaRadioSection {
    key: string;
    title?: React.ReactNode;
    options: NewformaRadioOption[];
    className?: string;
    titleClassName?: string;
}

export interface NewformaRadioGroupProps
    extends Pick<RadioGroupProps, "name" | "value" | "defaultValue" | "layout" | "disabled" | "className" | "style"> {
    options?: NewformaRadioOption[];
    sections?: NewformaRadioSection[];
    onValueChange?: (value: string, data: RadioGroupOnChangeData) => void;
    dataTestId?: string;
    hintText?: React.ReactNode;
}

export const NewformaRadioGroup: React.FC<NewformaRadioGroupProps> = ({
    name,
    value,
    defaultValue,
    layout,
    disabled,
    className,
    style,
    options,
    sections,
    onValueChange,
    dataTestId,
    hintText,
}) => {
    const styles = useStyles();

    const handleChange = React.useCallback(
        (_ev: React.FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) => {
            onValueChange?.(data.value, data);
        },
        [onValueChange]
    );

    const resolvedSections: NewformaRadioSection[] =
        sections && sections.length > 0 ? sections : [{ key: "default", options: options ?? [] }];

    return (
        <>
            <RadioGroup
                data-testid={dataTestId}
                name={name}
                value={value}
                defaultValue={defaultValue}
                layout={layout}
                disabled={disabled}
                className={mergeClasses(styles.radioGroup, className)}
                style={style}
                onChange={handleChange}
            >
                {resolvedSections.map((section) => (
                    <div key={section.key} className={section.className}>
                        {section.title ? (
                            <div className={mergeClasses(styles.sectionTitle, section.titleClassName)}>
                                {section.title}
                            </div>
                        ) : null}
                        {section.options.map((option) => (
                            <NewformaRadio
                                key={`${section.key}-${option.value}`}
                                name={name}
                                value={option.value}
                                label={option.label ?? option.value}
                                disabled={option.disabled}
                                className={mergeClasses(styles.option, option.className)}
                                style={{ paddingRight: "5px", ...option.style }}
                            />
                        ))}
                    </div>
                ))}
            </RadioGroup>
            {hintText && (
                <Text size={200} className={styles.hintText} data-testid="keep-typing-hint">
                    {hintText}
                </Text>
            )}
        </>
    );
};
